import React from 'react';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';

import { useIsConnectorOnboardingEnabled } from '../hooks/useIsConnectorOnboardingEnabled.ts';

import { ConnectorPromoSidebarLoader } from '../../entry-points/ConnectorPromoSidebarLoader.ts';

/**
 * Number of days from enrolment during which the sidebar connector promo is
 * eligible to render. Shared by every side-nav consumer (legacy global nav and
 * nav4/GSN side nav) so the two navs stay in lock-step. Intentionally shorter
 * than the hook's default 30-day window used by the search surfaces.
 */
const SIDEBAR_PROMO_DAYS = 14;

/**
 * Single gated entry point for rendering the connector promo in a side nav.
 *
 * Owns the eligibility gate (`useIsConnectorOnboardingEnabled`) so both the
 * legacy global navigation and the nav4 (GSN) side navigation render the promo
 * the same way: `<ConnectorPromoSidebarEntry isNav4? />`. The gate runs before
 * the lazy loader so the promo chunk is only fetched for eligible users, and it
 * satisfies the package invariant that consumers gate on
 * `useIsConnectorOnboardingEnabled` before any surface calls
 * `useConnectorPromoConfig`.
 *
 * The lazy loader is wrapped in a TransparentErrorBoundary so a chunk-load
 * failure is contained here instead of propagating up and blanking the host
 * side nav.
 */
export function ConnectorPromoSidebarEntry({
	isNav4,
}: {
	/** Set when rendered in the nav4 (GSN) side nav so the item wraps itself in a MenuList. */
	isNav4?: boolean;
} = {}): React.JSX.Element | null {
	const { isEnabled } = useIsConnectorOnboardingEnabled(SIDEBAR_PROMO_DAYS);

	if (!isEnabled) {
		return null;
	}

	return (
		<TransparentErrorBoundary attribution={Attribution.GROWTH_CFIND}>
			<ConnectorPromoSidebarLoader isNav4={isNav4} />
		</TransparentErrorBoundary>
	);
}
